import {
    takeEvery,
    put,
    select,
    call,
    all
} from 'redux-saga/effects';
import fp from 'lodash/fp';
import * as filterHeaderSelectors from '../../../webcommon/header/filterheader/selectors';
import actionFactory from '../../../webcommon/header/filterheader/actions';

import * as appSelectors from '../../../app/selectors';
import * as actions from './actions';
import * as selectors from './selectors';
import * as constants from './constants';
import { filterHeaderId as filterSummaryHeaderId } from '../../summary/constants';
import { restructureListByType } from '../../../api/adapters/filterAdapters';

const filterHeaderId = constants.ImImpactSummary;
const filterHeaderActions = actionFactory(filterHeaderId);
const filterSummaryHeaderActions = actionFactory(filterSummaryHeaderId);

const COUNTERPARTY = 'counterparty';
const CSA = 'csa';

export function* initialise() {
    const initialised = yield select(selectors.getInitialised);
    const savedFilters = yield select(selectors.getSavedFilters);
    const selectedFilters = yield select(filterHeaderSelectors.getFilters, filterSummaryHeaderId);
    const activeFilters = fp.filter(filter => filter.definition.id !== 'runDate')(selectedFilters);
    const csaFilterIndex = fp.findIndex(filter => filter.definition.id === CSA)(activeFilters);
    const counterpartyFilterIndex = fp.findIndex(filter => filter.definition.id === COUNTERPARTY)(activeFilters);
    const filters = fp.flow(
        fp.set(`${counterpartyFilterIndex}.value`, savedFilters.counterparties),
        fp.set(`${csaFilterIndex}.value`, savedFilters.csas)
    )(activeFilters);
    if (!initialised) {
        yield put(filterHeaderActions.setFilterDefinitions(constants.filterDefinitions));
        yield put(filterHeaderActions.setDefaultFilters(filters));
        yield put(actions.setInitialise());
        yield put(filterHeaderActions.resetFilters());
    }
}

export function* setAvailableCsasByCounterparty(counterparties = []) {
    const potentialCsaValues = yield select(appSelectors.getCsas);
    const allowedCsas = counterparties && counterparties.length
        ? potentialCsaValues.filter(csa => counterparties.includes(csa.counterparty))
        : potentialCsaValues;

    // get the selected filters in the summary filter header
    const activeSummaryFilters = yield select(filterHeaderSelectors.getFilters, filterSummaryHeaderId);
    const csaSummaryFilterIndex = fp.findIndex(filter => filter.definition.id === CSA, activeSummaryFilters);
    const activeCsa = activeSummaryFilters[csaSummaryFilterIndex].value;

    // set the selected filters to be disabled in compare csa list
    const activeCsaIndex = fp.findIndex(csa => csa.value === activeCsa[0])(allowedCsas);
    const csas = fp.flow(
        fp.map(fp.set('disabled', false)),
        fp.set(`[${activeCsaIndex}].disabled`, true),
        restructureListByType
    )(allowedCsas);
    yield put(filterHeaderActions.setAvailableListValues(CSA, csas));
}


export function* getCsas() {
    const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
    const counterpartyFilter = filters.find(filter => filter.definition.id === COUNTERPARTY);
    yield call(setAvailableCsasByCounterparty, counterpartyFilter.value);
}

export function* getCounterparties() {
    // create list of counterarty objects with type and disabled set to false
    const counterpartyList = yield select(appSelectors.getCounterparties);
    const counterpartiesDisabled = counterpartyList.map(counterparty => fp.set('disabled', false)(counterparty));
    // set selected counterparty to disabled
    const activeSummaryFilters = yield select(filterHeaderSelectors.getFilters, filterSummaryHeaderId);
    const activeCounterparty = activeSummaryFilters[1].value;
    const selectedCounterpartyIndex = fp.findIndex(counterparty => counterparty.value === activeCounterparty[0])(counterpartiesDisabled);
    const counterparties = fp.set(`[${selectedCounterpartyIndex}].disabled`, true)(counterpartiesDisabled);
    // structure counterparties on type
    const restructuredCounterparties = restructureListByType(counterparties);
    yield put(filterHeaderActions.setAvailableListValues(COUNTERPARTY, restructuredCounterparties));
}


export function* getAvailableValues(action) {
    switch (action.filterId) {
        case CSA:
            yield call(getCsas);
            break;
        case COUNTERPARTY:
            yield call(getCounterparties);
            break;
        default:
    }
}

export function* handleUpdate(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised) {
        if (action.filterId === COUNTERPARTY) {
            yield call(setAvailableCsasByCounterparty, action.value);
            const counterparties = action.value;
            const potentialCsaValues = yield select(appSelectors.getCsas);
            const allowedCsas = counterparties && counterparties.length
                ? potentialCsaValues.filter(csa => counterparties.includes(csa.counterparty))
                : potentialCsaValues;

            // find the currently selected csa in the summary header and set it to disabled
            const activeSummaryFilters = yield select(filterHeaderSelectors.getFilters, filterSummaryHeaderId);
            const activecsa = fp.find(filter => filter.definition.id === CSA)(activeSummaryFilters);
            const selectedCsaIndex = fp.findIndex(csa => csa.value === activecsa.value[0])(allowedCsas);
            const csasDisabled = allowedCsas.map(fp.set('disabled', false));
            const csas = fp.set(`[${selectedCsaIndex}].disabled`, true)(csasDisabled);

            // find currently active filters in the compare csa filter header
            const activeFilters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
            const csaIndex = fp.findIndex(filter => filter.definition.id === CSA)(activeFilters);
            const currentCsas = activeFilters[csaIndex].value;
            // find any csas that have been unchecked
            const csaList = csas.map(csa => csa.title);
            const removedFilters = fp.compact(currentCsas.map(csa => (csaList.includes(csa) ? null : csa)));

            const structuredCsas = restructureListByType(csas);

            if (csas.length === counterparties.length) {
                const newFilters = fp.set(`[${csaIndex}].value`, csaList)(activeFilters);
                yield put(filterHeaderActions.setFilters(newFilters));
                yield put(actions.compareCsasRequest(csaList));
            } else if (allowedCsas.length - activeFilters[csaIndex].value.length === 1 && !removedFilters.length) {
                const newFilters = fp.set(`[${csaIndex}].value`, csaList)(activeFilters);
                yield put(filterHeaderActions.setFilters(newFilters));
                yield put(actions.compareCsasRequest(csaList));
            } else if (removedFilters.length) {
                yield put(filterHeaderActions.setAvailableListValues(CSA, structuredCsas));
                const removeRequests = fp.filter(csa => !removedFilters.includes(csa))(currentCsas);
                yield put(actions.compareCsasRequest(removeRequests));
                const filters = fp.set(`[${csaIndex}].value`, removeRequests)(activeFilters);
                yield put(filterHeaderActions.setFilters(filters));
                if (structuredCsas.length !== removeRequests.length) {
                    yield put(filterHeaderActions.openFilter(CSA));
                }
            } else {
                yield put(filterHeaderActions.setAvailableListValues(CSA, structuredCsas));
                yield put(filterHeaderActions.openFilter(CSA));
            }
        } else if (action.filterId === CSA && Array.isArray(action.value) && action.value.length) {
            yield put(actions.compareCsasRequest(action.value));
        } else if (action.type === filterHeaderActions.RESET_FILTERS) {
            const filters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
            if (filters[0].value) {
                const counterpartyFilterValue = fp.flow(
                    fp.find(filter => fp.get('definition.id', filter) === COUNTERPARTY),
                    fp.get('value')
                )(filters);
                yield call(setAvailableCsasByCounterparty, counterpartyFilterValue);
            }
        }
    }
}

export function* setSelectedFilters(action) {
    const initialised = yield select(selectors.getInitialised);
    if (initialised && action.value.length) {
        yield call(getCounterparties);
        yield call(getCsas);

        const selectedFilters = yield select(filterHeaderSelectors.getFilters, filterSummaryHeaderId);
        const counterpartyFilterIndex = fp.findIndex(filter => filter.definition.id === COUNTERPARTY)(selectedFilters);
        const csaFilterIndex = fp.findIndex(filter => filter.definition.id === CSA)(selectedFilters);

        const csaCounterpartyList = yield select(appSelectors.getCsas);

        if (action.filterId === COUNTERPARTY) {
            const csaIndex = fp.findIndex(csa => csa.counterparty === selectedFilters[counterpartyFilterIndex].value[0])(csaCounterpartyList);
            const newFilters = fp.set(`[${csaFilterIndex}].value`, [csaCounterpartyList[csaIndex].title])(selectedFilters);
            yield put(filterHeaderActions.setFilters(newFilters));
        } else if (action.filterId === CSA) {
            // Set the corresponding counterparty to the csa as disabled
            const counterpartySelected = fp.find(csa => csa.title === action.value[0])(csaCounterpartyList);
            const counterpartyList = yield select(appSelectors.getCounterparties);
            const counterpartiesDisabled = counterpartyList.map(counterparty => fp.set('disabled', false)(counterparty));
            const selectedCounterpartyIndex = fp.findIndex(counterparty => counterparty.value === counterpartySelected.counterparty)(counterpartiesDisabled);
            const counterparties = fp.set(`[${selectedCounterpartyIndex}].disabled`, true)(counterpartiesDisabled);
            const restructuredCounterparties = restructureListByType(counterparties);
            yield put(filterHeaderActions.setAvailableListValues(COUNTERPARTY, restructuredCounterparties));

            const counterpartyIndex = fp.findIndex(csa => csa.title === selectedFilters[csaFilterIndex].value[0])(csaCounterpartyList);
            const newFilters = fp.set(`[${counterpartyFilterIndex}].value`, [csaCounterpartyList[counterpartyIndex].counterparty])(selectedFilters);
            yield put(filterHeaderActions.setFilters(newFilters));
        }
    }
}

export function* setSavedFilters(action) {
    const initialised = yield select(selectors.getInitialised);
    const selectedFilters = yield select(filterHeaderSelectors.getFilters, filterHeaderId);
    if (initialised) {
        const counterpartyFilterIndex = fp.findIndex(filter => filter.definition.id === COUNTERPARTY)(selectedFilters);
        const csaFilterIndex = fp.findIndex(filter => filter.definition.id === CSA)(selectedFilters);
        const newFilters = fp.flow(
            fp.set(`[${csaFilterIndex}].value`, action.csas),
            fp.set(`[${counterpartyFilterIndex}].value`, action.counterparties)
        )(selectedFilters);
        yield call(setAvailableCsasByCounterparty, action.counterparties);
        yield put(filterHeaderActions.setFilters(newFilters));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(actions.INITIALISE, initialise),
        takeEvery([
            filterHeaderActions.UPDATE_FILTER,
            filterHeaderActions.REMOVE_FILTER,
            filterHeaderActions.RESET_FILTERS
        ], handleUpdate),
        takeEvery(
            filterHeaderActions.FETCH_AVAILABLE_LIST_VALUES,
            getAvailableValues
        ),
        takeEvery(
            filterSummaryHeaderActions.UPDATE_FILTER,
            setSelectedFilters
        ),
        takeEvery(
            actions.SET_SAVED_FILTERS,
            setSavedFilters
        )
    ]);
}
